import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, ArrowRight, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type {
  EditorStep,
  EditorCardStep,
  EditorCardGroupStep,
  EditorTransition,
  TransitionCondition,
  SingleCondition,
  CompoundCondition,
  ScoreComparisonCondition,
} from "@/types/activity";

/* ─── Card info for response_value conditions ───────────── */

export interface ActivityCardInfo {
  /** card localId or existingCardId */
  cardId: string;
  label: string;
  templateCode: string;
  /** For single_choice / multiple_choice: the options array */
  options?: { id: string; label: string }[];
}

/** Group info for score conditions targeting a whole group */
export interface ActivityGroupInfo {
  groupId: string; // existingGroupId or step localId
  label: string;
}

interface TransitionEditorProps {
  step: EditorStep;
  allSteps: EditorStep[];
  /** All cards across the activity for response_value condition */
  activityCards: ActivityCardInfo[];
  /** All card groups across the activity for score conditions */
  activityGroups?: ActivityGroupInfo[];
  onChange: (transitions: EditorTransition[]) => void;
  /** If set, only show this single transition (edge selection mode) */
  singleTransitionIndex?: number;
  /** When true, all controls are read-only */
  readOnly?: boolean;
}

const SCORE_OPERATORS = [
  { value: "lt", label: "< (inférieur)" },
  { value: "lte", label: "≤ (inférieur ou égal)" },
  { value: "gt", label: "> (supérieur)" },
  { value: "gte", label: "≥ (supérieur ou égal)" },
  { value: "eq", label: "= (égal)" },
];

function getStepLabel(s: EditorStep): string {
  if (s.type === "card") {
    const base = s.templateLabel || "Carte";
    const title = getContentTitle(s.content);
    return title ? `${base} | ${title}` : base;
  }
  const title = (s as EditorCardGroupStep).title || "Groupe";
  return title.length > 25 ? title.slice(0, 22) + "…" : title;
}

function getContentTitle(content: Record<string, unknown>): string {
  for (const val of Object.values(content)) {
    if (typeof val === "string" && val.trim()) {
      const t = val.trim();
      return t.length > 20 ? t.slice(0, 17) + "…" : t;
    }
  }
  return "";
}

/* ─── Sortable transition item ──────────────────────────── */

function SortableTransitionItem({
  transition,
  index,
  otherSteps,
  activityCards,
  activityGroups,
  onUpdate,
  onRemove,
  onConditionTypeChange,
  onConditionUpdate,
  onSetCompound,
  readOnly,
}: {
  transition: EditorTransition;
  index: number;
  otherSteps: EditorStep[];
  activityCards: ActivityCardInfo[];
  activityGroups: ActivityGroupInfo[];
  onUpdate: (idx: number, updates: Partial<EditorTransition>) => void;
  onRemove: (idx: number) => void;
  onConditionTypeChange: (idx: number, type: string, subIndex?: number) => void;
  onConditionUpdate: (idx: number, updates: Partial<TransitionCondition>, subIndex?: number) => void;
  onSetCompound: (idx: number, logic: "and" | "or" | "none") => void;
  readOnly?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition: dndTransition,
    isDragging,
  } = useSortable({ id: `transition-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: dndTransition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="rounded-md border border-border bg-muted/30 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="cursor-grab text-muted-foreground hover:text-foreground shrink-0"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <ArrowRight className="h-4 w-4 text-primary shrink-0" />
        <Select
          value={transition.toStepLocalId}
          onValueChange={(v) => onUpdate(index, { toStepLocalId: v })}
          disabled={readOnly}
        >
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="Étape cible..." />
          </SelectTrigger>
          <SelectContent>
            {otherSteps.map((s) => (
              <SelectItem key={s.localId} value={s.localId}>
                {getStepLabel(s)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!readOnly && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive shrink-0"
            onClick={() => onRemove(index)}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>

      <TransitionConditionEditor
        transition={transition}
        index={index}
        activityCards={activityCards}
        activityGroups={activityGroups}
        onConditionTypeChange={onConditionTypeChange}
        onConditionUpdate={onConditionUpdate}
        onSetCompound={onSetCompound}
      />
    </div>
  );
}

/* ─── Condition editor (shared between single & list mode) ─ */

function TransitionConditionEditor({
  transition,
  index,
  activityCards,
  activityGroups,
  onConditionTypeChange,
  onConditionUpdate,
  onSetCompound,
}: {
  transition: EditorTransition;
  index: number;
  activityCards: ActivityCardInfo[];
  activityGroups: ActivityGroupInfo[];
  onConditionTypeChange: (idx: number, type: string, subIndex?: number) => void;
  onConditionUpdate: (idx: number, updates: Partial<TransitionCondition>, subIndex?: number) => void;
  onSetCompound: (idx: number, logic: "and" | "or" | "none") => void;
}) {
  const condition = transition.condition;
  const isCompound = condition?.type === "compound";
  const compoundCondition = isCompound ? (condition as CompoundCondition) : null;

  // For simple (non-compound) conditions
  const simpleCondition = !isCompound ? condition : null;

  return (
    <div className="space-y-2">
      {/* Compound mode selector */}
      <div className="flex items-center gap-2">
        <Label className="text-xs shrink-0">Mode :</Label>
        <Select
          value={isCompound ? compoundCondition!.logic : "none"}
          onValueChange={(v) => onSetCompound(index, v as "and" | "or" | "none")}
        >
          <SelectTrigger className="flex-1 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Condition simple</SelectItem>
            <SelectItem value="and">ET (toutes les conditions)</SelectItem>
            <SelectItem value="or">OU (au moins une condition)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Simple condition */}
      {!isCompound && (
        <SingleConditionEditor
          condition={simpleCondition}
          activityCards={activityCards}
          activityGroups={activityGroups}
          onTypeChange={(type) => onConditionTypeChange(index, type)}
          onUpdate={(updates) => onConditionUpdate(index, updates)}
        />
      )}

      {/* Compound conditions */}
      {isCompound && compoundCondition && (
        <div className="space-y-3 pl-2 border-l-2 border-primary/30">
          {compoundCondition.conditions.map((subCond, subIdx) => (
            <div key={subIdx} className="space-y-2 rounded-md border border-border/50 bg-background/50 p-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  Condition {subIdx + 1}
                </span>
                {compoundCondition.conditions.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 text-destructive"
                    onClick={() => {
                      const newConds = compoundCondition.conditions.filter((_, i) => i !== subIdx);
                      onConditionUpdate(index, { conditions: newConds } as any);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <SingleConditionEditor
                condition={subCond}
                activityCards={activityCards}
                activityGroups={activityGroups}
                onTypeChange={(type) => onConditionTypeChange(index, type, subIdx)}
                onUpdate={(updates) => onConditionUpdate(index, updates, subIdx)}
              />
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => {
              const newConds = [...compoundCondition.conditions, { type: "score" as const, operator: "gte" as const, value: 0 }];
              onConditionUpdate(index, { conditions: newConds } as any);
            }}
          >
            <Plus className="mr-1 h-3 w-3" />
            Ajouter une condition
          </Button>
        </div>
      )}
    </div>
  );
}

/* ─── Single condition editor ────────────────────────────── */

function SingleConditionEditor({
  condition,
  activityCards,
  activityGroups,
  onTypeChange,
  onUpdate,
}: {
  condition: TransitionCondition | null;
  activityCards: ActivityCardInfo[];
  activityGroups: ActivityGroupInfo[];
  onTypeChange: (type: string) => void;
  onUpdate: (updates: Partial<TransitionCondition>) => void;
}) {
  const selectedCard = condition?.type === "response_value"
    ? activityCards.find((c) => c.cardId === (condition as any).card_id)
    : null;

  const condType = condition?.type || "none";

  return (
    <>
      {/* Condition type selector */}
      <div className="flex items-center gap-2">
        <Label className="text-xs shrink-0">Condition :</Label>
        <Select
          value={condType}
          onValueChange={(v) => onTypeChange(v)}
        >
          <SelectTrigger className="flex-1 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Aucune (inconditionnelle)</SelectItem>
            <SelectItem value="score">Score</SelectItem>
            <SelectItem value="score_comparison">Comparaison de scores</SelectItem>
            <SelectItem value="response_value">Valeur de réponse</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Score condition */}
      {condition?.type === "score" && (
        <ScoreConditionEditor
          condition={condition as any}
          activityCards={activityCards}
          activityGroups={activityGroups}
          onChange={onUpdate}
        />
      )}

      {/* Score comparison condition */}
      {condition?.type === "score_comparison" && (
        <ScoreComparisonEditor
          condition={condition as ScoreComparisonCondition}
          activityCards={activityCards}
          activityGroups={activityGroups}
          onChange={(updates) => onUpdate(updates as any)}
        />
      )}

      {/* Response value condition */}
      {condition?.type === "response_value" && (
        <ResponseValueEditor
          condition={condition as any}
          activityCards={activityCards}
          selectedCard={selectedCard ?? null}
          onChange={(updates) => onUpdate(updates as any)}
        />
      )}
    </>
  );
}

/* ─── Score condition editor ─────────────────────────────── */

function ScoreConditionEditor({
  condition,
  activityCards,
  activityGroups,
  onChange,
}: {
  condition: { type: "score"; operator: string; value: number; card_id?: string; card_group_id?: string };
  activityCards: ActivityCardInfo[];
  activityGroups: ActivityGroupInfo[];
  onChange: (updates: Record<string, unknown>) => void;
}) {
  // Determine current source selection: __previous__, card:<id>, or group:<id>
  const currentSource = condition.card_group_id
    ? `group:${condition.card_group_id}`
    : condition.card_id
      ? `card:${condition.card_id}`
      : "__previous__";

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label className="text-xs shrink-0">Source :</Label>
        <Select
          value={currentSource}
          onValueChange={(v) => {
            if (v === "__previous__") {
              onChange({ card_id: undefined, card_group_id: undefined });
            } else if (v.startsWith("group:")) {
              onChange({ card_id: undefined, card_group_id: v.replace("group:", "") });
            } else if (v.startsWith("card:")) {
              onChange({ card_group_id: undefined, card_id: v.replace("card:", "") });
            }
          }}
        >
          <SelectTrigger className="flex-1 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__previous__">Carte/groupe précédent</SelectItem>
            {activityGroups.length > 0 && (
              <>
                <SelectItem value="__groups_header__" disabled className="text-xs font-semibold text-muted-foreground">
                  ── Groupes ──
                </SelectItem>
                {activityGroups.map((g) => (
                  <SelectItem key={`group:${g.groupId}`} value={`group:${g.groupId}`}>
                    🗂 {g.label} (somme)
                  </SelectItem>
                ))}
              </>
            )}
            {activityCards.length > 0 && (
              <>
                <SelectItem value="__cards_header__" disabled className="text-xs font-semibold text-muted-foreground">
                  ── Cartes ──
                </SelectItem>
                {activityCards.map((c) => (
                  <SelectItem key={`card:${c.cardId}`} value={`card:${c.cardId}`}>
                    {c.label}
                  </SelectItem>
                ))}
              </>
            )}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <Select
          value={condition.operator}
          onValueChange={(v) => onChange({ operator: v })}
        >
          <SelectTrigger className="w-32 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SCORE_OPERATORS.map((op) => (
              <SelectItem key={op.value} value={op.value}>
                {op.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="number"
          className="w-20 h-8 text-xs"
          value={condition.value ?? 0}
          onChange={(e) => onChange({ value: Number(e.target.value) })}
        />
      </div>
    </div>
  );
}

/* ─── Score comparison condition editor ──────────────────── */

function ScoreComparisonEditor({
  condition,
  activityCards,
  activityGroups,
  onChange,
}: {
  condition: ScoreComparisonCondition;
  activityCards: ActivityCardInfo[];
  activityGroups: ActivityGroupInfo[];
  onChange: (updates: Record<string, unknown>) => void;
}) {
  // Build combined options for cards and groups
  const sourceOptions = [
    ...activityGroups.map((g) => ({ value: `group:${g.groupId}`, label: `🗂 ${g.label} (somme)` })),
    ...activityCards.map((c) => ({ value: `card:${c.cardId}`, label: c.label })),
  ];

  const currentA = (condition as any).card_group_id_a
    ? `group:${(condition as any).card_group_id_a}`
    : `card:${condition.card_id_a}`;
  const currentB = (condition as any).card_group_id_b
    ? `group:${(condition as any).card_group_id_b}`
    : `card:${condition.card_id_b}`;

  const handleSourceChange = (side: "a" | "b", value: string) => {
    if (value.startsWith("group:")) {
      const groupId = value.replace("group:", "");
      if (side === "a") {
        onChange({ card_id_a: "", card_group_id_a: groupId });
      } else {
        onChange({ card_id_b: "", card_group_id_b: groupId });
      }
    } else {
      const cardId = value.replace("card:", "");
      if (side === "a") {
        onChange({ card_id_a: cardId, card_group_id_a: undefined });
      } else {
        onChange({ card_id_b: cardId, card_group_id_b: undefined });
      }
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label className="text-xs shrink-0">Source A :</Label>
        <Select value={currentA} onValueChange={(v) => handleSourceChange("a", v)}>
          <SelectTrigger className="flex-1 h-8 text-xs">
            <SelectValue placeholder="Sélectionner..." />
          </SelectTrigger>
          <SelectContent>
            {sourceOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <Label className="text-xs shrink-0">Opérateur :</Label>
        <Select value={condition.operator} onValueChange={(v) => onChange({ operator: v })}>
          <SelectTrigger className="flex-1 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SCORE_OPERATORS.map((op) => (
              <SelectItem key={op.value} value={op.value}>
                Score A {op.label.split(" ")[0]} Score B
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <Label className="text-xs shrink-0">Source B :</Label>
        <Select value={currentB} onValueChange={(v) => handleSourceChange("b", v)}>
          <SelectTrigger className="flex-1 h-8 text-xs">
            <SelectValue placeholder="Sélectionner..." />
          </SelectTrigger>
          <SelectContent>
            {sourceOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

/* ─── Response value condition editor ───────────────────── */

function ResponseValueEditor({
  condition,
  activityCards,
  selectedCard,
  onChange,
}: {
  condition: { type: "response_value"; card_id: string; operator: string; values: string[]; logic?: "or" | "and" };
  activityCards: ActivityCardInfo[];
  selectedCard: ActivityCardInfo | null;
  onChange: (updates: Record<string, unknown>) => void;
}) {
  const hasOptions = selectedCard?.options && selectedCard.options.length > 0;
  const values = condition.values ?? [];

  return (
    <div className="space-y-2">
      {/* Card selector */}
      <div className="flex items-center gap-2">
        <Label className="text-xs shrink-0">Carte :</Label>
        <Select
          value={condition.card_id || ""}
          onValueChange={(v) => onChange({ card_id: v, values: [] })}
        >
          <SelectTrigger className="flex-1 h-8 text-xs">
            <SelectValue placeholder="Sélectionner une carte..." />
          </SelectTrigger>
          <SelectContent>
            {activityCards.map((c) => (
              <SelectItem key={c.cardId} value={c.cardId}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Options checkboxes */}
      {selectedCard && hasOptions && (
        <div className="space-y-1.5">
          <Label className="text-xs">Valeurs sélectionnées :</Label>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {selectedCard.options!.map((opt) => {
              const selected = values.includes(opt.id);
              return (
                <label key={opt.id} className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox
                    checked={selected}
                    onCheckedChange={(checked) => {
                      const updated = checked
                        ? [...values, opt.id]
                        : values.filter((v) => v !== opt.id);
                      onChange({ values: updated });
                    }}
                  />
                  <span>{opt.label || opt.id}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* No options message */}
      {selectedCard && !hasOptions && (
        <p className="text-xs text-muted-foreground italic">
          Cette carte ne propose pas d'options configurables.
        </p>
      )}

      {/* OR / AND selector when 2+ values */}
      {values.length >= 2 && (
        <div className="flex items-center gap-2">
          <Label className="text-xs shrink-0">Condition :</Label>
          <Select
            value={condition.logic || "or"}
            onValueChange={(v) => onChange({ logic: v })}
          >
            <SelectTrigger className="flex-1 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="or">OU (au moins une)</SelectItem>
              <SelectItem value="and">ET (toutes)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

/* ─── Main component ────────────────────────────────────── */

export default function TransitionEditor({
  step,
  allSteps,
  activityCards,
  activityGroups = [],
  onChange,
  singleTransitionIndex,
  readOnly,
}: TransitionEditorProps) {
  const transitions = step.transitions;
  const otherSteps = allSteps.filter((s) => s.localId !== step.localId);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const addTransition = () => {
    const nextOrder = transitions.length;
    onChange([
      ...transitions,
      {
        toStepLocalId: otherSteps[0]?.localId || "",
        condition: null,
        orderIndex: nextOrder,
      },
    ]);
  };

  const removeTransition = (idx: number) => {
    const updated = transitions.filter((_, i) => i !== idx);
    onChange(updated.map((t, i) => ({ ...t, orderIndex: i })));
  };

  const updateTransition = (idx: number, updates: Partial<EditorTransition>) => {
    const updated = [...transitions];
    updated[idx] = { ...updated[idx], ...updates };
    onChange(updated);
  };

  const setConditionType = (idx: number, type: string, subIndex?: number) => {
    let newCondition: TransitionCondition | null = null;

    if (type === "none") {
      newCondition = null;
    } else if (type === "score") {
      newCondition = { type: "score", operator: "gte", value: 0 };
    } else if (type === "response_value") {
      // Default to the previous card in the activity
      const stepIndex = allSteps.findIndex((s) => s.localId === step.localId);
      let defaultCardId = "";
      if (step.type === "card") {
        defaultCardId = step.existingCardId || step.localId;
      } else if (step.type === "card_group") {
        const group = step as EditorCardGroupStep;
        if (group.cards?.length) {
          const lastCard = group.cards[group.cards.length - 1];
          defaultCardId = lastCard.existingCardId || lastCard.localId;
        }
      }
      if (!defaultCardId) {
        for (let i = stepIndex - 1; i >= 0; i--) {
          const prev = allSteps[i];
          if (prev.type === "card") {
            defaultCardId = prev.existingCardId || prev.localId;
            break;
          }
          const group = prev as EditorCardGroupStep;
          if (group.cards?.length) {
            const lastCard = group.cards[group.cards.length - 1];
            defaultCardId = lastCard.existingCardId || lastCard.localId;
            break;
          }
        }
      }
      if (!defaultCardId && activityCards.length > 0) {
        defaultCardId = activityCards[0].cardId;
      }
      newCondition = { type: "response_value", card_id: defaultCardId, operator: "eq", values: [] };
    } else if (type === "score_comparison") {
      const cardA = activityCards[0]?.cardId || "";
      const cardB = activityCards[1]?.cardId || activityCards[0]?.cardId || "";
      newCondition = { type: "score_comparison", card_id_a: cardA, card_id_b: cardB, operator: "gt" };
    }

    // If we're inside a compound condition, update only the sub-condition
    if (subIndex !== undefined) {
      const current = transitions[idx].condition;
      if (current?.type === "compound") {
        const compound = current as CompoundCondition;
        const newConds = [...compound.conditions];
        newConds[subIndex] = newCondition as SingleCondition || { type: "score", operator: "gte", value: 0 };
        updateTransition(idx, { condition: { ...compound, conditions: newConds } });
        return;
      }
    }

    updateTransition(idx, { condition: newCondition });
  };

  const updateCondition = (idx: number, updates: Partial<TransitionCondition>, subIndex?: number) => {
    const current = transitions[idx].condition;
    if (!current) return;

    if (subIndex !== undefined && current.type === "compound") {
      const compound = current as CompoundCondition;
      const newConds = [...compound.conditions];
      newConds[subIndex] = { ...newConds[subIndex], ...updates } as SingleCondition;
      updateTransition(idx, { condition: { ...compound, conditions: newConds } });
      return;
    }

    updateTransition(idx, { condition: { ...current, ...updates } as TransitionCondition });
  };

  const setCompoundMode = (idx: number, mode: "and" | "or" | "none") => {
    const current = transitions[idx].condition;

    if (mode === "none") {
      // Convert compound to simple: take the first sub-condition or null
      if (current?.type === "compound") {
        const compound = current as CompoundCondition;
        updateTransition(idx, { condition: compound.conditions[0] || null });
      }
      // If already simple, do nothing
      return;
    }

    // Convert to compound
    if (current?.type === "compound") {
      // Just change the logic
      updateTransition(idx, { condition: { ...current, logic: mode } });
    } else {
      // Wrap current condition (or create a default) in a compound
      const firstCondition: SingleCondition = (current && (current as any).type !== "compound")
        ? current as SingleCondition
        : { type: "score", operator: "gte", value: 0 };
      updateTransition(idx, {
        condition: {
          type: "compound",
          logic: mode,
          conditions: [firstCondition],
        },
      });
    }
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = Number(String(active.id).replace("transition-", ""));
      const newIndex = Number(String(over.id).replace("transition-", ""));
      const reordered = [...transitions];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);
      onChange(reordered.map((t, i) => ({ ...t, orderIndex: i })));
    },
    [transitions, onChange]
  );

  if (otherSteps.length === 0) {
    return (
      <div className="text-xs text-muted-foreground italic">
        Ajoutez d'autres étapes pour créer des transitions.
      </div>
    );
  }

  // ── Single transition mode (edge selected) ──
  if (singleTransitionIndex !== undefined && singleTransitionIndex >= 0 && singleTransitionIndex < transitions.length) {
    const t = transitions[singleTransitionIndex];
    const idx = singleTransitionIndex;

    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <ArrowRight className="h-4 w-4 text-primary shrink-0" />
          <Select
            value={t.toStepLocalId}
            onValueChange={(v) => updateTransition(idx, { toStepLocalId: v })}
            disabled={readOnly}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Étape cible..." />
            </SelectTrigger>
            <SelectContent>
              {otherSteps.map((s) => (
                <SelectItem key={s.localId} value={s.localId}>
                  {getStepLabel(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!readOnly && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive shrink-0"
              onClick={() => removeTransition(idx)}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>

        {!readOnly && (
          <TransitionConditionEditor
            transition={t}
            index={idx}
            activityCards={activityCards}
            activityGroups={activityGroups}
            onConditionTypeChange={setConditionType}
            onConditionUpdate={updateCondition}
            onSetCompound={setCompoundMode}
          />
        )}
      </div>
    );
  }

  // ── List mode with DnD (node selected) ──
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Transitions sortantes</Label>
        {!readOnly && (
          <Button type="button" variant="outline" size="sm" onClick={addTransition}>
            <Plus className="mr-1 h-3 w-3" />
            Transition
          </Button>
        )}
      </div>

      {transitions.length === 0 && (
        <p className="text-xs text-muted-foreground">
          Aucune transition (fin de l'activité après cette étape).
        </p>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={transitions.map((_, i) => `transition-${i}`)}
          strategy={verticalListSortingStrategy}
        >
          {transitions.map((t, idx) => (
            <SortableTransitionItem
              key={`transition-${idx}`}
              transition={t}
              index={idx}
              otherSteps={otherSteps}
              activityCards={activityCards}
              activityGroups={activityGroups}
              onUpdate={updateTransition}
              onRemove={removeTransition}
              onConditionTypeChange={setConditionType}
              onConditionUpdate={updateCondition}
              onSetCompound={setCompoundMode}
              readOnly={readOnly}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
}
